<template>
  <div class="drawer-subview cancel-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Cancel booking</h5>
    </div>

    <div class="view-body">
      <!-- Warning -->
      <div class="subview-warning" v-if="!booking.associatedBookingId">
        <i class="tim-icons icon-alert-circle-exc"></i>
        <p>Cancelling this booking will result in the time slot becoming available to book. Any amount paid will need to be manually refunded to the customer.</p>
      </div>
      <div class="subview-warning" v-else>
        <i class="tim-icons icon-alert-circle-exc"></i>
        <p>Booking will only cancel for selected group. Other groups will still retain the booking. Any amount paid will need to be manually refunded to the cancelled group.</p>
      </div>

      <!-- Status selector -->
      <div class="subview-field" v-if="!booking.associatedBookingId">
        <label class="field-label">Status for freed time slot</label>
        <el-select
          v-model="selectedStatus"
          class="select-primary w-100"
          value-key="id"
          placeholder="Status"
        >
          <el-option
            v-for="(status, index) in slotStatuses"
            class="select-primary"
            :label="status.label"
            :value="{ id: status.id, label: status.label, status: status.status }"
            :key="'CSTATUS' + index"
          ></el-option>
        </el-select>
      </div>

      <!-- Waiver options -->
      <div class="subview-field" v-if="waiverCount > 0 && !booking.associatedBookingId">
        <label class="field-label">This booking has {{ waiverCount }} waiver(s) attached</label>
        <div class="waiver-options">
          <div
            class="waiver-option"
            :class="{ selected: deleteWaivers === 'keep' }"
            @click="deleteWaivers = 'keep'"
          >
            <el-radio v-model="deleteWaivers" label="keep">Keep waivers on file</el-radio>
            <small class="option-desc">Waivers remain attached to this time slot. Future bookings will inherit them.</small>
          </div>
          <div
            class="waiver-option"
            :class="{ selected: deleteWaivers === 'delete' }"
            @click="deleteWaivers = 'delete'"
          >
            <el-radio v-model="deleteWaivers" label="delete">Delete waivers</el-radio>
            <small class="option-desc">Waivers will be permanently removed. The time slot will be clean for new bookings.</small>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="subview-actions">
        <base-button type="secondary" size="sm" @click="$emit('back')">Go back</base-button>
        <base-button type="primary" size="sm" :disabled="loading" @click="confirmCancel">
          <span v-if="loading"><i class="el-icon-loading mr-1"></i> Cancelling...</span>
          <span v-else>Cancel booking</span>
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_LOCATION } from '@/api-config';
import { Auth } from 'aws-amplify';
import { Select, Option, Radio } from 'element-ui';

export default {
  name: 'CancelView',

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Radio.name]: Radio
  },

  props: {
    booking: { type: Object, default: () => ({}) },
    companyId: { type: [Number, String], default: null },
    companyGroupId: { type: [Number, String], default: null },
    slotStatuses: {
      type: Array,
      default: () => [
        { id: 1, label: 'Available', status: 'available' },
        { id: 2, label: 'Blocked', status: 'blocked' },
        { id: 3, label: 'Call to book', status: 'call_to_book' }
      ]
    }
  },

  data() {
    return {
      loading: false,
      selectedStatus: { id: 1, label: 'Available', status: 'available' },
      deleteWaivers: 'keep',
      waiverCount: 0
    };
  },

  watch: {
    booking: {
      immediate: true,
      handler() {
        this.selectedStatus = { id: 1, label: 'Available', status: 'available' };
        this.deleteWaivers = 'keep';
        this.fetchWaiverCount();
      }
    }
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    async fetchWaiverCount() {
      if (!this.booking.id || !this.companyId || !this.companyGroupId) return;

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/${this.booking.id}/waivers`;
        const response = await axios.get(url, config);
        const waivers = response.data.users || response.data.waivers || [];
        this.waiverCount = waivers.length;
      } catch (error) {
        console.error('Failed to fetch waivers:', error);
        this.waiverCount = 0;
      }
    },

    async confirmCancel() {
      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const shouldDelete = this.deleteWaivers === 'delete';
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/${this.booking.id}?new_status=${this.selectedStatus.status}&delete_waivers=${shouldDelete}`;

        await axios.delete(url, config);

        this.$notify({
          type: 'success',
          message: 'Booking has been cancelled',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });

        this.$emit('cancelled');
      } catch (error) {
        console.error('Failed to cancel booking:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to cancel booking',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// Shared sub-view anatomy (header/body/warning/field/actions/textarea) comes
// from the .drawer-subview partial (dashboard/custom/_drawer-subview.scss).
// Only cancel-specific pieces live here.
.waiver-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.waiver-option {
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }

  &.selected {
    border-color: #e14eca;
    background: rgba(225, 78, 202, 0.1);
  }
}

.option-desc {
  display: block;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
  margin-left: 24px;
  font-size: 12px;
}
</style>

<style lang="scss">
.white-content {
  .cancel-view .waiver-option {
    border-color: #e3e3e3;
    &:hover { border-color: #ccc; }
    &.selected { border-color: #e14eca; background: rgba(225, 78, 202, 0.05); }
  }

  .cancel-view .option-desc {
    color: #9A9A9A;
  }

  .cancel-view .el-radio__label {
    color: #344675;
  }
}
</style>
